import { useState, useEffect, useCallback } from 'react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import SectionHeader from '../../components/admin/SectionHeader';
import StatCard from '../../components/admin/StatCard';
import DataTable from '../../components/admin/DataTable';

// Their UTM convention: source = site, medium = section, campaign = page, content = CTA.
const DIMENSIONS = [
  { key: 'utm_source', label: 'Source', hint: 'utm_source · e.g. website' },
  { key: 'utm_medium', label: 'Section', hint: 'utm_medium · e.g. pricing' },
  { key: 'utm_campaign', label: 'Page', hint: 'utm_campaign · e.g. home' },
  { key: 'utm_content', label: 'CTA', hint: 'utm_content · e.g. get-started' },
  { key: 'landing_page', label: 'Landing page', hint: 'first page visited' },
];

const PERIODS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: 'All', days: 0 },
];

const fmtDate = (d) => d.toISOString().slice(0, 10);
function rangeToDates(days) {
  if (!days) return {};
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - days);
  return { date_from: fmtDate(from), date_to: fmtDate(to) };
}

function AdminAnalytics({ apiFetch, toast }) {
  const [dimension, setDimension] = useState('utm_source');
  const [period, setPeriod] = useState(30);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const dates = rangeToDates(period);
    try {
      const p = new URLSearchParams({ dimension });
      if (dates.date_from) p.set('date_from', dates.date_from);
      if (dates.date_to) p.set('date_to', dates.date_to);
      const [aRes, tRes] = await Promise.all([
        apiFetch(`/api/admin/analytics/attribution?${p.toString()}`),
        apiFetch(`/api/admin/analytics/signup-trend?${new URLSearchParams(dates).toString()}`),
      ]);
      if (aRes.ok) {
        const data = await aRes.json();
        setRows(data.rows || []);
        setTotals(data.totals || null);
      } else {
        toast.error('Error', 'Failed to load attribution');
      }
      if (tRes.ok) {
        const t = await tRes.json();
        setTrend((t.trend || []).map(r => ({ day: r.day, signups: Number(r.signups) || 0 })));
      }
    } catch {
      toast.error('Error', 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, dimension, period, toast]);

  useEffect(() => { load(); }, [load]);

  const dimLabel = DIMENSIONS.find(d => d.key === dimension)?.label || 'Value';
  const topRows = rows.slice(0, 8).map(r => ({
    name: (r.dimension || '(none)').length > 18 ? r.dimension.slice(0, 17) + '…' : r.dimension,
    signups: r.signups,
    subscriptions: r.subscriptions,
  }));

  const columns = [
    { key: 'dimension', header: dimLabel, render: r => <span style={{ fontWeight: 600 }}>{r.dimension || '(none)'}</span> },
    { key: 'signups', header: 'Signups', width: '90px', cellStyle: { fontWeight: 600 } },
    { key: 'checkouts', header: 'Checkouts', width: '100px' },
    { key: 'subscriptions', header: 'Subscriptions', width: '120px' },
    { key: 'trialing', header: 'On trial', width: '90px', render: r => <span style={{ color: '#f59e0b' }}>{r.trialing}</span> },
    { key: 'active_paid', header: 'Paid', width: '80px', render: r => <span style={{ color: '#1aad9f', fontWeight: 600 }}>{r.active_paid}</span> },
    { key: 'signup_to_sub_rate', header: 'Signup→Sub', width: '110px', render: r => `${r.signup_to_sub_rate}%` },
  ];

  return (
    <div>
      <SectionHeader
        title="Marketing Attribution"
        description="Which page, section and CTA drives signups — and which convert to trials and paid."
        actions={
          <div className="admin-btn-group">
            {PERIODS.map(p => (
              <button key={p.label} className={`admin-btn admin-btn--sm ${period === p.days ? 'admin-btn--primary' : 'admin-btn--secondary'}`} onClick={() => setPeriod(p.days)}>
                {p.label}
              </button>
            ))}
            <button className="admin-btn admin-btn--sm admin-btn--secondary" onClick={load} title="Refresh">
              <i className="fa-solid fa-arrows-rotate" />
            </button>
          </div>
        }
      />

      {/* Stat cards from totals */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        <StatCard label="Signups" value={totals?.signups ?? 0} icon="fa-solid fa-user-plus" variant="primary" />
        <StatCard label="Reached checkout" value={totals?.checkouts ?? 0} icon="fa-solid fa-cart-shopping" />
        <StatCard label="Subscriptions" value={totals?.subscriptions ?? 0} icon="fa-solid fa-file-signature" />
        <StatCard label="On trial" value={totals?.trialing ?? 0} icon="fa-solid fa-hourglass-half" />
        <StatCard label="Paid / active" value={totals?.active_paid ?? 0} icon="fa-solid fa-circle-check" />
        <StatCard label="Signup → Sub" value={`${totals?.signup_to_sub_rate ?? 0}%`} icon="fa-solid fa-percent" />
      </div>

      {/* Dimension selector */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
        <span style={{ fontSize: '12px', color: 'var(--admin-text-muted)', marginRight: '4px' }}>Break down by:</span>
        {DIMENSIONS.map(d => (
          <button key={d.key} className={`admin-btn admin-btn--sm ${dimension === d.key ? 'admin-btn--primary' : 'admin-btn--secondary'}`} onClick={() => setDimension(d.key)} title={d.hint}>
            {d.label}
          </button>
        ))}
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
        <div className="admin-card" style={{ padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: 600, color: 'var(--admin-text-primary)' }}>Signups over time</h3>
          {trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={trend}>
                <defs>
                  <linearGradient id="signupGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
                <XAxis dataKey="day" tick={{ fill: 'var(--admin-text-muted)', fontSize: 11 }} axisLine={{ stroke: 'var(--admin-border)' }} />
                <YAxis allowDecimals={false} tick={{ fill: 'var(--admin-text-muted)', fontSize: 11 }} axisLine={{ stroke: 'var(--admin-border)' }} />
                <Tooltip contentStyle={{ background: 'var(--admin-card)', border: '1px solid var(--admin-border)', borderRadius: '8px', fontSize: '12px' }} labelStyle={{ color: 'var(--admin-text-secondary)' }} />
                <Area type="monotone" dataKey="signups" stroke="#3b82f6" strokeWidth={2} fill="url(#signupGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--admin-text-muted)' }}>No data</div>
          )}
        </div>

        <div className="admin-card" style={{ padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: 600, color: 'var(--admin-text-primary)' }}>Top {dimLabel} — signups vs subscriptions</h3>
          {topRows.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={topRows}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
                <XAxis dataKey="name" tick={{ fill: 'var(--admin-text-muted)', fontSize: 11 }} axisLine={{ stroke: 'var(--admin-border)' }} interval={0} angle={-20} textAnchor="end" height={50} />
                <YAxis allowDecimals={false} tick={{ fill: 'var(--admin-text-muted)', fontSize: 11 }} axisLine={{ stroke: 'var(--admin-border)' }} />
                <Tooltip contentStyle={{ background: 'var(--admin-card)', border: '1px solid var(--admin-border)', borderRadius: '8px', fontSize: '12px' }} labelStyle={{ color: 'var(--admin-text-secondary)' }} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Bar dataKey="signups" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                <Bar dataKey="subscriptions" fill="#1aad9f" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--admin-text-muted)' }}>No data</div>
          )}
        </div>
      </div>

      {/* Breakdown table */}
      <div className="admin-card">
        <div style={{ padding: '16px 16px 0', marginBottom: '8px' }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: 'var(--admin-text-primary)' }}>Funnel by {dimLabel}</h3>
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--admin-text-muted)' }}>
            Attributed by each customer's first-touch UTM. "Paid / active" counts subscriptions currently active or past-due.
          </p>
        </div>
        <DataTable columns={columns} data={rows} loading={loading} emptyMessage="No attribution data yet" emptyIcon="fa-solid fa-chart-pie" />
      </div>
    </div>
  );
}

export default AdminAnalytics;
